// SelectVariantModal.tsx
import React, { useState, useEffect } from "react"
import { Modal, Typography, List, Checkbox } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { faTimeline, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Project } from "stores/projectStore"
import { Variant, SelectedVariant } from "types/questionnaire.types"

/**
 * Props interface for the SelectVariantModal component
 */
interface SelectVariantModalProps {
  /** The project object containing project details */
  project: Project
  /** The array of variant objects available for selection */
  variants: Variant[]
  /** The currently selected variant object */
  selectedVariant: SelectedVariant
  /** Function to set the selected variant */
  setSelectedVariant: (variant: Variant | { name: string }) => void
  /** Function to handle the deletion of a variant */
  handleDeleteVariant: (variant: Variant) => Promise<void>
  /** Function to refresh the project data */
  refresh: (id: string) => Promise<void>
  /** Function to set the visibility of the preview */
  setPreviewVisible: (visible: boolean) => void
  /** Function to save before switching variants (for collaborative mode) */
  onBeforeSwitch?: () => Promise<void>
  /** Whether the button should be disabled */
  isDisabled?: boolean
}

/**
 * Interface for variant list items (includes Master variant)
 */
interface VariantListItem {
  name: string
  languages: string[]
}

/**
 * SelectVariantModal is a modal component for selecting a variant within a project.
 * Users can choose from a list of available variants, including a default "Master" variant.
 *
 * @component
 * @param props - The component props
 * @returns The rendered SelectVariantModal component
 */
const SelectVariantModal: React.FC<SelectVariantModalProps> = ({
  project,
  variants,
  handleDeleteVariant,
  selectedVariant,
  setSelectedVariant,
  refresh,
  setPreviewVisible,
  onBeforeSwitch,
  isDisabled = false,
}) => {
  const { Title } = Typography
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [tempSelectedVariant, setTempSelectedVariant] =
    useState<SelectedVariant>(selectedVariant)

  /**
   * Handles the variant selection and updates the state.
   *
   * @function selectVariantCallback
   */
  const selectVariantCallback = async (): Promise<void> => {
    setPreviewVisible(false)

    // Save current content before switching (for collaborative mode)
    if (onBeforeSwitch) {
      await onBeforeSwitch()
    }

    // If tempSelectedVariant is empty object (Master), pass Master object
    if (Object.keys(tempSelectedVariant).length === 0) {
      setSelectedVariant({ name: "Master" })
    } else {
      setSelectedVariant(tempSelectedVariant as Variant)
    }

    setIsModalVisible(false)
    // Don't call refresh in collaborative mode - Yjs handles content sync
    // and calling refresh causes editor to reload
    if (!onBeforeSwitch) {
      refresh(project.id)
    }
  }

  /**
   * Cancels the variant selection and closes the modal.
   *
   * @function cancelVariant
   */
  const cancelVariant = (): void => {
    setIsModalVisible(false)
  }

  /**
   * Shows the modal for selecting a variant.
   *
   * @function showModal
   */
  const showModal = (): void => {
    setIsModalVisible(true)
  }

  /**
   * Handles the selection of a variant.
   *
   * @function handleVariantSelection
   * @param variant - The selected variant object
   */
  const handleVariantSelection = (variant: VariantListItem): void => {
    if (variant.name === "Master") {
      setTempSelectedVariant({})
    } else {
      setTempSelectedVariant(variant as Variant)
    }
  }

  // Sync tempSelectedVariant when selectedVariant prop changes
  useEffect(() => {
    setTempSelectedVariant(selectedVariant)
  }, [selectedVariant])

  // Update variants list when the variants prop changes
  useEffect(() => {}, [variants])

  // Add default "Master" variant
  const masterVariant: VariantListItem = {
    name: "Master",
    languages: project.Languages,
  }
  const updatedVariants: VariantListItem[] = [masterVariant, ...variants]

  return (
    <>
      <DataQButton
        className="w-3/6 shadow-sm flex items-center"
        onClick={showModal}
        type="primary"
        isDisabled={isDisabled}
      >
        <FontAwesomeIcon icon={faTimeline} className="mr-2" />
        Selected Variant:
        <p className="font-semibold whitespace-nowrap inline-block ml-1">
          {Object.keys(selectedVariant).length === 0
            ? "Master"
            : (selectedVariant as Variant).name}
        </p>
      </DataQButton>
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginTop: "0.5em" }} level={4}>
          Select Variant
        </Title>
        <div className="max-h-96 overflow-auto">
          <List
            className="variant-select"
            itemLayout="horizontal"
            dataSource={updatedVariants}
            renderItem={(item: VariantListItem) => (
              <List.Item
                onClick={() => handleVariantSelection(item)}
                className="cursor-pointer hover:bg-gray-100 rounded-2xl"
                actions={[
                  <Checkbox
                    key={item.name}
                    className="mr-2"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.stopPropagation()
                      handleVariantSelection(item)
                    }}
                    checked={
                      (tempSelectedVariant as Variant).name
                        ? (tempSelectedVariant as Variant).name === item.name
                        : item.name === "Master"
                    }
                  />,
                  item.name !== "Master" && (
                    <FontAwesomeIcon
                      key={`delete-${item.name}`}
                      icon={faTrash}
                      className="text-red-500 mr-4"
                      onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                        e.stopPropagation()
                        handleDeleteVariant(item as Variant)
                      }}
                    />
                  ),
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  className="p-2 ml-2"
                  title={item.name}
                  description={`Languages: ${item.languages
                    .map((language: string) => language)
                    .join(", ")}`}
                />
              </List.Item>
            )}
          />
        </div>
        <div className="flex flex-row mt-5">
          <DataQButton
            className="ml-auto mr-2"
            type="primary"
            onClick={selectVariantCallback}
          >
            Select Variant
          </DataQButton>
          <DataQButton type="secondary" onClick={cancelVariant}>
            Cancel
          </DataQButton>
        </div>
      </Modal>
    </>
  )
}

export default SelectVariantModal
